'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { Check, Gift, ShieldCheck, Timer, Sunrise, Sun } from 'lucide-react';
import Image from 'next/image';
import { LeatherHeader, visualFor } from '@/components/rental-visuals';
import BookingButton from '@/components/booking-button';
import rentalDataEn from '@/content/mtb-rental-packs-summer-2025.json';
import rentalDataFr from '@/content/mtb-rental-packs-summer-2025.fr.json';
import rentalDataNl from '@/content/mtb-rental-packs-summer-2025.nl.json';
import rentalDataEs from '@/content/mtb-rental-packs-summer-2025.es.json';
import rentalDataIt from '@/content/mtb-rental-packs-summer-2025.it.json';
type RentalDuration = '2h' | 'halfday' | '1day';
type RentalPack = {
  id: string;
  name: string;
  model?: string;
  category: string;
  description: string;
  features: string[];
  pricing: Record<RentalDuration, number>;
  ageRange?: string;
  products?: Array<{
    name: string;
    description: string;
    photo: string;
  }>;
};
const getDurationLabels = (locale: string): Record<RentalDuration, string> => {
  if (locale === 'fr') {
    return {
      '2h': '2H',
      'halfday': '1/2J',
      '1day': '1JOUR'
    };
  } else if (locale === 'es') {
    return {
      '2h': '2H',
      'halfday': '1/2D',
      '1day': '1DÍA'
    };
  } else if (locale === 'nl') {
    return {
      '2h': '2U',
      'halfday': '1/2D',
      '1day': '1DAG'
    };
  } else if (locale === 'it') {
    return {
      '2h': '2H',
      'halfday': '1/2G',
      '1day': '1GIORNO'
    };
  } else {
    // English default
    return {
      '2h': '2H',
      'halfday': '1/2D',
      '1day': '1DAY'
    };
  }
};
type SummerRentalPricingProps = {
  locale: string;
  additionalEquipmentTitle?: string;
  pricesTitle?: string;
  adultMTBLabel?: string;
  juniorMTBLabel?: string;
  featuresLabel?: string;
  benefitsLabel?: string;
  freeLabel?: string;
  couponBanner?: string;
  showAllDurations?: boolean;
};
export default function SummerRentalPricing(t0) {
  const $ = _c(70);
  const {
    locale,
    additionalEquipmentTitle,
    pricesTitle,
    adultMTBLabel,
    juniorMTBLabel,
    featuresLabel,
    benefitsLabel,
    freeLabel,
    couponBanner,
    showAllDurations
  } = t0;
  const [selectedDuration, setSelectedDuration] = useState("1day");
  const [selectedCategory, setSelectedCategory] = useState("adult");
  let t1;
  if ($[0] !== locale) {
    t1 = () => {
      switch (locale) {
        case "fr":
          {
            return rentalDataFr;
          }
        case "nl":
          {
            return rentalDataNl;
          }
        case "es":
          {
            return rentalDataEs;
          }
        case "it":
          {
            return rentalDataIt;
          }
        default:
          {
            return rentalDataEn;
          }
      }
    };
    $[0] = locale;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const getTranslatedData = t1;
  let t10;
  let t11;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== additionalEquipmentTitle || $[3] !== adultMTBLabel || $[4] !== benefitsLabel || $[5] !== couponBanner || $[6] !== featuresLabel || $[7] !== freeLabel || $[8] !== getTranslatedData || $[9] !== juniorMTBLabel || $[10] !== locale || $[11] !== pricesTitle || $[12] !== selectedCategory || $[13] !== selectedDuration || $[14] !== showAllDurations) {
    const translatedData = getTranslatedData();
    let t12;
    if ($[25] !== locale) {
      t12 = getDurationLabels(locale);
      $[25] = locale;
      $[26] = t12;
    } else {
      t12 = $[26];
    }
    const durationLabels = t12;
    const bestValueLabel = ({
      fr: "Meilleure offre",
      en: "Best value",
      nl: "Beste keuze",
      es: "Mejor precio",
      it: "Miglior prezzo"
    } as Record<string, string>)[locale] || "Best value";
    const perBikeLabel = ({
      fr: "par v\xE9lo",
      en: "per bike",
      nl: "per fiets",
      es: "por bici",
      it: "per bici"
    } as Record<string, string>)[locale] || "per bike";
    const currentPacks = selectedCategory === "adult" ? translatedData.adultPacks : translatedData.juniorPacks;
    const insurancePack = translatedData.insurancePack as RentalPack;
    const allPacks = [...currentPacks, insurancePack];
    const durationKeys = Object.keys(durationLabels) as RentalDuration[];
    const currentPack = currentPacks[0] as RentalPack | undefined;
    t10 = "rental-prices";
    t11 = "py-16 px-4 scroll-mt-20";
    t6 = "max-w-7xl mx-auto";
    const t13 = pricesTitle || `MTB Rental Prices ${translatedData.season}`;
    let t14;
    if ($[27] !== t13) {
      t14 = <h2 className="text-4xl md:text-6xl font-bold font-[family-name:var(--font-bebas-neue)] mb-4">{t13}</h2>;
      $[27] = t13;
      $[28] = t14;
    } else {
      t14 = $[28];
    }
    let t15;
    if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" /></svg>;
      $[29] = t15;
    } else {
      t15 = $[29];
    }
    const t16 = couponBanner || "Les Granges Residents: -20% with your coupon code!";
    let t17;
    if ($[30] !== t16) {
      t17 = <div className="flex justify-center mt-8 mb-4"><div className="inline-flex items-center gap-2 bg-white text-neutral-900 px-6 py-3 rounded-full font-bold text-lg shadow-md border border-foreground/25">{t15}{t16}</div></div>;
      $[30] = t16;
      $[31] = t17;
    } else {
      t17 = $[31];
    }
    if ($[32] !== t14 || $[33] !== t17) {
      t7 = <div className="text-center mb-12">{t14}{t17}</div>;
      $[32] = t14;
      $[33] = t17;
      $[34] = t7;
    } else {
      t7 = $[34];
    }
    let t18;
    if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = () => setSelectedCategory("adult");
      $[35] = t18;
    } else {
      t18 = $[35];
    }
    const t19 = `px-7 py-2.5 rounded-full text-base font-bold transition-all duration-200 ${selectedCategory === "adult" ? "bg-gradient-to-r from-nav-gradient-from to-nav-gradient-to text-white shadow-md" : "text-foreground/70 hover:text-foreground hover:bg-background"}`;
    const t20 = adultMTBLabel || "Adult MTB";
    let t21;
    if ($[36] !== t19 || $[37] !== t20) {
      t21 = <button onClick={t18} className={t19}>{t20}</button>;
      $[36] = t19;
      $[37] = t20;
      $[38] = t21;
    } else {
      t21 = $[38];
    }
    let t22;
    if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = () => setSelectedCategory("junior");
      $[39] = t22;
    } else {
      t22 = $[39];
    }
    const t23 = `px-7 py-2.5 rounded-full text-base font-bold transition-all duration-200 ${selectedCategory === "junior" ? "bg-gradient-to-r from-nav-gradient-from to-nav-gradient-to text-white shadow-md" : "text-foreground/70 hover:text-foreground hover:bg-background"}`;
    const t24 = juniorMTBLabel || "Junior MTB";
    let t25;
    if ($[40] !== t23 || $[41] !== t24) {
      t25 = <button onClick={t22} className={t23}>{t24}</button>;
      $[40] = t23;
      $[41] = t24;
      $[42] = t25;
    } else {
      t25 = $[42];
    }
    if ($[43] !== t21 || $[44] !== t25) {
      t8 = <div className="flex justify-center mb-8"><div className="inline-flex items-center gap-1 rounded-full border border-foreground/25 bg-muted/60 p-1.5 shadow-sm">{t21}{t25}</div></div>;
      $[43] = t21;
      $[44] = t25;
      $[45] = t8;
    } else {
      t8 = $[45];
    }
    t9 = showAllDurations && currentPack ? <><div className="mt-8 mb-6 max-w-5xl mx-auto overflow-hidden rounded-2xl border border-border bg-card shadow-sm"><div className="p-6 pt-8 md:p-8"><div className="flex flex-col gap-6 md:flex-row md:items-stretch md:gap-8">{currentPack.products?.[0]?.photo && <Image src={currentPack.products[0].photo} alt={currentPack.model || ""} width={420} height={320} className="aspect-[4/3] w-full shrink-0 rounded-xl object-cover shadow-md ring-1 ring-border md:aspect-auto md:h-auto md:w-72" />}<div className="min-w-0 flex-1">{currentPack.model && <h3 className="mb-3 text-4xl font-bold uppercase font-[family-name:var(--font-bebas-neue)] leading-none tracking-wide text-foreground sm:text-5xl">{currentPack.model}</h3>}<p className="mb-5 text-base leading-relaxed text-muted-foreground">{currentPack.description}</p><div className="grid gap-x-6 gap-y-3 sm:grid-cols-2">{currentPack.features.map(_temp)}</div></div></div><div className="mt-10 mb-6 flex items-center gap-4"><span className="h-px flex-1 bg-border" /><span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{locale === "fr" ? "Options de location" : locale === "es" ? "Opciones de alquiler" : locale === "nl" ? "Verhuuropties" : locale === "it" ? "Opzioni di noleggio" : "Rental options"}</span><span className="h-px flex-1 bg-border" /></div><div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">{durationKeys.map((duration, i) => {
              const tiers = [{
                color: "#6b8ea3",
                accent: "text-slate-600",
                ring: "ring-slate-300",
                badge: "bg-slate-500",
                Icon: Timer
              }, {
                color: "#5a9070",
                accent: "text-emerald-700",
                ring: "ring-emerald-300",
                badge: "bg-emerald-600",
                Icon: Sunrise
              }, {
                color: "#b5893f",
                accent: "text-amber-700",
                ring: "ring-amber-300",
                badge: "bg-amber-600",
                Icon: Sun
              }];
              const tier = tiers[i % tiers.length];
              const isBest = i === durationKeys.length - 1;
              return <div key={duration} className={`group relative flex flex-col overflow-hidden rounded-xl bg-card transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${isBest ? `ring-2 ${tier.ring} shadow-md` : "border border-border shadow-sm"}`}><div className="flex py-5 items-center justify-center gap-2.5 border-b-[3px] border-dashed bg-[url('/leather.jpg')] bg-cover bg-center shadow-[inset_0_0_0_1px_rgba(42,31,16,0.55),inset_0_2px_2px_rgba(255,240,214,0.30),inset_0_-12px_22px_-8px_rgba(0,0,0,0.55)]" style={{
                  borderColor: "#efe4c8"
                }}><tier.Icon className="h-8 w-8 rounded-full bg-[#f4ead0] p-2 box-content shadow-[inset_0_-2px_3px_rgba(74,49,20,0.20),0_1px_2px_rgba(0,0,0,0.30)] ring-1 ring-[#5b4326]/35" style={{
                    color: tier.color
                  }} strokeWidth={2.5} /><h3 className="text-3xl sm:text-4xl font-bold font-[family-name:var(--font-bebas-neue)] tracking-wide text-black [text-shadow:0_1px_0_rgba(255,240,214,0.55)]">{durationLabels[duration]}</h3></div><div className="flex flex-col flex-grow p-6 text-center">{isBest && <span className={`mx-auto -mt-9 mb-3 inline-flex items-center rounded-full ${tier.badge} px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-md ring-4 ring-card`}>{bestValueLabel}</span>}<div className="flex items-baseline justify-center gap-1"><span className="text-5xl font-bold leading-none tabular-nums">{currentPack.pricing[duration]}</span><span className={`text-xl font-bold ${tier.accent}`}>€</span></div><div className="mt-1.5 mb-5 text-xs font-medium uppercase tracking-wide text-muted-foreground">{perBikeLabel}</div><div className="mt-auto"><BookingButton season="summer" currentLocale={locale} variant="gradient" gradientClass="from-nav-gradient-from to-nav-gradient-to" fullWidth={true} size="md" /></div></div></div>;
            })}</div><div className="mt-10 mb-6 flex items-center gap-4"><span className="h-px flex-1 bg-border" /><span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{locale === "fr" ? "Ajouter une protection" : locale === "es" ? "A\xF1adir protecci\xF3n" : locale === "nl" ? "Bescherming toevoegen" : locale === "it" ? "Aggiungi protezione" : "Add protection"}</span><span className="h-px flex-1 bg-border" /></div><div className="flex flex-col items-center gap-4 rounded-xl border border-border bg-muted/30 p-5 text-center sm:flex-row sm:text-left"><ShieldCheck className="h-11 w-11 shrink-0 rounded-full bg-[#f4ead0] p-2.5 box-content ring-1 ring-[#5b4326]/35 text-emerald-600" strokeWidth={2} /><div className="min-w-0 flex-1"><h4 className="text-2xl font-bold font-[family-name:var(--font-bebas-neue)] tracking-wide text-foreground">{insurancePack.name}</h4><p className="text-sm text-muted-foreground line-clamp-2">{insurancePack.description}</p></div><div className="flex shrink-0 items-baseline gap-1"><span className="text-4xl font-bold leading-none tabular-nums">{insurancePack.pricing["1day"]}</span><span className="text-lg font-bold text-emerald-600">€</span><span className="ml-1 text-xs text-muted-foreground">{locale === "fr" ? "/ jour" : locale === "es" ? "/ d\xEDa" : locale === "nl" ? "/ dag" : locale === "it" ? "/ giorno" : "/ day"}</span></div></div></div></div></> : <><div className="mb-12"><div className="flex flex-wrap justify-center gap-2 md:gap-3">{(Object.keys(durationLabels) as RentalDuration[]).map(duration_0 => <button key={duration_0} onClick={() => setSelectedDuration(duration_0)} className={`relative px-4 py-2 md:px-6 md:py-3 rounded-lg font-semibold transition-all ${selectedDuration === duration_0 ? "bg-primary text-primary-foreground shadow-lg scale-105" : "bg-muted text-foreground hover:bg-muted/80 hover:shadow-md"}`}>{durationLabels[duration_0]}</button>)}</div></div><div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">{allPacks.map(pack => {
          const displayPrice = pack.pricing[selectedDuration];
          const isInsurance = pack.id === "insurance";
          return <div key={pack.id} className={`group relative bg-card border-2 border-border rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden ${isInsurance ? "md:col-span-2 lg:col-span-1" : ""}`}><LeatherHeader name={pack.name} {...visualFor(pack.id)} variant="leather" /><div className="p-8">{pack.model && <div className="text-center mb-4"><p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{pack.model}</p></div>}<div className="flex flex-col items-center gap-2.5 mb-6"><div className="flex items-baseline gap-2"><span className="text-5xl font-bold">{displayPrice}€</span><span className="text-muted-foreground">/ {durationLabels[selectedDuration]}</span></div></div><div className="mb-6"><BookingButton season="summer" currentLocale={locale} variant="gradient" gradientClass="from-nav-gradient-from to-nav-gradient-to" fullWidth={true} size="md" /></div><div className="mb-6 min-h-[140px]"><p className="text-base leading-relaxed text-muted-foreground">{pack.description}</p></div><div className="space-y-3"><h4 className="text-sm font-semibold text-foreground uppercase tracking-wider">{isInsurance ? benefitsLabel || "Benefits" : featuresLabel || "Features"}</h4>{pack.features.map((feature_0, idx_0) => <div key={idx_0} className="flex items-start gap-3"><Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isInsurance ? "text-emerald-600 " : "text-primary"}`} /><span className="text-sm text-muted-foreground leading-relaxed">{feature_0}</span></div>)}</div></div></div>;
        })}</div></>;
    t4 = "mt-16";
    const t26 = additionalEquipmentTitle || "Additional Equipment";
    if ($[46] !== t26) {
      t5 = <div className="text-center mb-10"><h3 className="text-3xl md:text-4xl font-bold font-[family-name:var(--font-bebas-neue)]">{t26}</h3></div>;
      $[46] = t26;
      $[47] = t5;
    } else {
      t5 = $[47];
    }
    t2 = "flex flex-wrap justify-center gap-6";
    let t27;
    if ($[48] !== durationLabels || $[49] !== freeLabel || $[50] !== locale || $[51] !== selectedDuration) {
      t27 = item => {
        const displayPrice_0 = item.pricing[selectedDuration];
        const standalonePrice = item.standalonePricing?.[selectedDuration];
        return <div key={item.id} className="bg-card border-2 border-border rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden w-full md:w-[calc(50%-12px)] lg:w-[calc(25%-18px)]"><LeatherHeader name={item.name} {...visualFor(item.id)} size="sm" variant="leather" /><div className="p-6"><div className="flex flex-col items-center gap-2.5 mb-5">{displayPrice_0 === 0 && standalonePrice ? <><div className="flex items-baseline justify-center gap-x-2"><span className="text-2xl font-bold">{standalonePrice}€</span><span className="text-sm text-muted-foreground">/ {durationLabels[selectedDuration]}</span></div><div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1"><span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide text-emerald-600 ring-1 ring-emerald-500/25"><Gift className="w-3.5 h-3.5" />{freeLabel || "FREE"}</span><span className="text-sm text-muted-foreground">{locale === "fr" ? "avec location v\xE9lo" : locale === "es" ? "con alquiler de bici" : locale === "nl" ? "bij fietsverhuur" : locale === "it" ? "con noleggio bici" : "with bike rental"}</span></div></> : displayPrice_0 === 0 ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-3 py-1 text-sm font-bold uppercase tracking-wide text-emerald-600 ring-1 ring-emerald-500/25"><Gift className="w-4 h-4" />{freeLabel || "FREE"}</span> : <div className="flex items-baseline justify-center gap-2"><span className="text-3xl font-bold">{displayPrice_0}€</span><span className="text-sm text-muted-foreground">/ {durationLabels[selectedDuration]}</span></div>}</div><div className="mb-4"><BookingButton season="summer" currentLocale={locale} variant="primary" fullWidth={true} size="sm" /></div><p className="text-sm text-muted-foreground mb-4">{item.description}</p><div className="space-y-2">{item.features.slice(0, 3).map(_temp2)}</div></div></div>;
      };
      $[48] = durationLabels;
      $[49] = freeLabel;
      $[50] = locale;
      $[51] = selectedDuration;
      $[52] = t27;
    } else {
      t27 = $[52];
    }
    t3 = translatedData.otherRentals.map(t27);
    $[2] = additionalEquipmentTitle;
    $[3] = adultMTBLabel;
    $[4] = benefitsLabel;
    $[5] = couponBanner;
    $[6] = featuresLabel;
    $[7] = freeLabel;
    $[8] = getTranslatedData;
    $[9] = juniorMTBLabel;
    $[10] = locale;
    $[11] = pricesTitle;
    $[12] = selectedCategory;
    $[13] = selectedDuration;
    $[14] = showAllDurations;
    $[15] = t10;
    $[16] = t11;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t10 = $[15];
    t11 = $[16];
    t2 = $[17];
    t3 = $[18];
    t4 = $[19];
    t5 = $[20];
    t6 = $[21];
    t7 = $[22];
    t8 = $[23];
    t9 = $[24];
  }
  let t12;
  if ($[53] !== t2 || $[54] !== t3) {
    t12 = <div className={t2}>{t3}</div>;
    $[53] = t2;
    $[54] = t3;
    $[55] = t12;
  } else {
    t12 = $[55];
  }
  let t13;
  if ($[56] !== t12 || $[57] !== t4 || $[58] !== t5) {
    t13 = <div className={t4}>{t5}{t12}</div>;
    $[56] = t12;
    $[57] = t4;
    $[58] = t5;
    $[59] = t13;
  } else {
    t13 = $[59];
  }
  let t14;
  if ($[60] !== t13 || $[61] !== t6 || $[62] !== t7 || $[63] !== t8 || $[64] !== t9) {
    t14 = <div className={t6}>{t7}{t8}{t9}{t13}</div>;
    $[60] = t13;
    $[61] = t6;
    $[62] = t7;
    $[63] = t8;
    $[64] = t9;
    $[65] = t14;
  } else {
    t14 = $[65];
  }
  let t15;
  if ($[66] !== t10 || $[67] !== t11 || $[68] !== t14) {
    t15 = <div id={t10} className={t11}>{t14}</div>;
    $[66] = t10;
    $[67] = t11;
    $[68] = t14;
    $[69] = t15;
  } else {
    t15 = $[69];
  }
  return t15;
}
function _temp2(feature_1, idx_1) {
  return <div key={idx_1} className="flex items-start gap-2"><Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" /><span className="text-xs text-muted-foreground leading-relaxed">{feature_1}</span></div>;
}
function _temp(feature, idx) {
  return <div key={idx} className="flex items-start gap-2.5"><Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" /><span className="text-sm text-foreground/80 leading-relaxed">{feature}</span></div>;
}